import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { GetUserQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { GSHomeTEMP } from '../../borrower/routes/gsRoutesTEMP/Home';
import { GSRouterTemplateTEMP } from '../domains/page/ui/GSRouterTemplateTEMP';
import { CreditAgreementsRouter } from './creditAgreements/Router';
import { DeliverablesRoute } from './DeliverablesRoute';
import { EventsRoute } from './EventsRoute';
import { FundingRequestRouter } from './fundingRequests/Router';
import { PortfolioRouter } from './portfolio/Router';
import { ReportsRoute } from './ReportsRoute';
import { ResourcesCalculationTerminologiesRoute } from './ResourcesCalculationTerminologiesRoute';
import { SettingsRouter } from './settings/Router';
import { CapitalProviderTemplate } from 'capitalProvider/domains/page/ui/CapitalProviderTemplate';
import { isQAUser } from 'packages/common-frontend/borrowerEnabledConfig';
import { RouteTemplate } from 'packages/common-frontend/components/RouteTemplate';
import { heapIdentify } from 'packages/common-frontend/hooks/useHeap';
import { DeliverableDetailRoute } from 'packages/deliverables/ui/DeliverableDetail';

const isNotProduction = !commonUtils.EnvUtils.isProduction();

export const CapitalProviderRouter: React.FC<{
  userData: GetUserQueryGQLType;
}> = withLDConsumer()(({ userData, flags, ldClient }) => {
  useEffect(() => {
    if (ldClient !== undefined && userData?.user !== undefined) {
      ldClient.identify({
        kind: 'user',
        key: userData.user!.id,
        orgSlug: userData.user!.organization.slug,
        roleType: userData.user!.roleType,
        isInternalFinleyUser: isQAUser(userData.user!.email),
      });
    }
  }, [ldClient, userData]);

  if (!userData || !userData.user) {
    return null;
  }

  const { user } = userData;

  // identifies heap analytics info to logged in customer's email
  if (user && window.heap?.identify) {
    heapIdentify({ dashboardType: 'capitalProvider', user });
  }

  // every route is rapped with the old RouteTemplate or the new FullWidthRouteTemplate
  // once everything is moved to full width, can combine FullWidthRouteTemplate into CapitalProviderTemplate
  if (
    user?.organization?.slug === 'goldmanSachs' &&
    flags?.displayGoldmanSachsBackdoorDownloadRouter
  ) {
    return (
      <GSRouterTemplateTEMP>
        <Switch>
          <Route exact path="/">
            <RouteTemplate>
              <GSHomeTEMP borrowerName={`${user.firstName} ${user.lastName}`} />
            </RouteTemplate>
          </Route>
          <Route exact path="/deliverables/:deliverableId">
            <RouteTemplate>
              <DeliverableDetailRoute />
            </RouteTemplate>
          </Route>
          <Redirect to="/" />
        </Switch>
      </GSRouterTemplateTEMP>
    );
  }

  return (
    <CapitalProviderTemplate>
      <Switch>
        <Route exact path="/">
          <Redirect to="/portfolio" />
        </Route>
        <Route path="/portfolio">
          <PortfolioRouter />
        </Route>
        {isNotProduction && (
          <Route path="/credit-agreements">
            <CreditAgreementsRouter />
          </Route>
        )}
        <Route path="/deliverables">
          <DeliverablesRoute />
        </Route>
        <Route exact path="/reports">
          <ReportsRoute />
        </Route>
        <Route path="/funding-requests">
          <FundingRequestRouter organizationId={user.organizationId} />
        </Route>
        <Route path="/calculation-terminologies">
          <ResourcesCalculationTerminologiesRoute />
        </Route>
        <Route path="/settings">
          <SettingsRouter />
        </Route>
        <Route exact path="/events">
          <EventsRoute />
        </Route>
      </Switch>
    </CapitalProviderTemplate>
  );
});
